import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Mail, ShieldCheck } from 'lucide-react'
import { forgotPassword } from '@/api/services/auth.service'
import { PageMetadata } from '@/components/common/PageMetadata'
import { Button, Card, Input } from '@/components/ui'
import { brandAssets } from '@/config/brandAssets'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { useAuthStore } from '@/stores/useAuthStore'

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
})

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordPage(): React.ReactElement {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })

  const mutation = useMutation({
    mutationFn: forgotPassword,
    onSuccess: (_data, values) => {
      setSubmittedEmail(values.email)
    },
  })

  const onSubmit = (values: ForgotPasswordFormValues): void => {
    setSubmittedEmail(null)
    mutation.mutate({ email: values.email })
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="flex min-h-dvh min-w-0 flex-col overflow-x-hidden bg-white pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] lg:flex-row lg:flex-nowrap">
      <PageMetadata />
      <section className="relative order-2 flex w-full min-w-0 flex-col justify-between overflow-hidden bg-linear-to-br from-primary to-primary/90 px-4 py-8 text-white sm:px-6 sm:py-10 md:px-10 lg:order-1 lg:w-1/2 lg:px-12 lg:py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.22),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(17,24,39,0.16),transparent_38%)]" />
        <div className="relative z-10 mx-auto flex w-full max-w-4xl flex-1 flex-col justify-between">
          <div>
            <img src={brandAssets.logo} alt="KAAMCO" className="h-auto w-44 object-contain sm:w-56" />
            <p className="mt-8 max-w-xl text-3xl font-semibold leading-tight sm:text-4xl">
              Get a secure link to reset your password.
            </p>
            <p className="mt-4 max-w-lg text-sm text-white/85 sm:text-base">
              Enter your account email and we will send a one-time reset link if the account exists.
            </p>
          </div>

          <div className="relative mt-10 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/20 bg-white/12 p-4 backdrop-blur-md">
              <Mail className="h-5 w-5" aria-hidden />
              <p className="mt-3 text-sm font-semibold">Email reset</p>
              <p className="mt-1 text-xs text-white/80">
                The link goes to the same secure password setup page used for invites.
              </p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/12 p-4 backdrop-blur-md">
              <ShieldCheck className="h-5 w-5" aria-hidden />
              <p className="mt-3 text-sm font-semibold">Account protection</p>
              <p className="mt-1 text-xs text-white/80">
                We do not reveal whether an email is registered.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="order-1 flex w-full min-w-0 flex-col items-center justify-center bg-white px-4 py-8 sm:px-6 sm:py-10 lg:order-2 lg:w-1/2 lg:px-8 lg:py-16">
        <div className="w-full max-w-md">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
            Password reset
          </p>
          <Card
            className="w-full max-w-md border-gray-200 bg-white shadow-md dark:border-gray-200 dark:bg-white"
            contentClassName="p-5 sm:p-8"
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Forgot password?</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Enter your email and check your inbox for the reset link.
                </p>
              </div>

              {submittedEmail ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  If an account exists for {submittedEmail}, we sent a reset link.
                </div>
              ) : null}

              <Input
                label="Email"
                type="email"
                autoComplete="email"
                error={errors.email?.message}
                className="text-base md:text-sm dark:border-gray-300 dark:bg-gray-50 dark:text-gray-900 dark:placeholder:text-gray-500"
                {...register('email')}
              />

              {mutation.isError ? (
                <p className="text-sm text-danger" role="alert">
                  {getErrorMessage(mutation.error)}
                </p>
              ) : null}

              <Button
                type="submit"
                className="min-h-11 w-full touch-manipulation"
                loading={mutation.isPending}
              >
                Send reset link
              </Button>

              <p className="text-center text-xs text-gray-500">
                Remembered your password?{' '}
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Back to sign in
                </Link>
                .
              </p>
            </form>
          </Card>
        </div>
      </section>
    </div>
  )
}

ForgotPasswordPage.displayName = 'ForgotPasswordPage'
